import {
  useCanvasFileRecords,
  useFlowSessionFileRecords,
  useWorkspaceTree,
} from "@/api/fileRecords";
import { ROUTES } from "@/constants/routes";
import type { FileRecordActionHandlerDeps } from "@/object-actions/handlers/fileRecord";
import { useCanvasFileRecordNodeIds } from "@/pages/canvas/hooks/useCanvasFileRecordNodeIds";
import { useFileRecordCardActions } from "@/pages/workspaces/hooks/useFileRecordCardActions";
import type { FileRecord } from "@cohesive/api-client";
import { useMemo } from "react";
import { useMatch } from "react-router-dom";

function useFileRecordSiblingList(record: FileRecord): FileRecord[] {
  const workspaceQuery = useWorkspaceTree(
    record.class === "workspace" ? record.workspace_id : undefined,
  );
  const canvasQuery = useCanvasFileRecords(
    record.class === "canvas" ? record.canvas_id : undefined,
  );
  const flowSessionQuery = useFlowSessionFileRecords(
    record.class === "flow-session" ? record.flow_session_id : undefined,
  );

  return useMemo(() => {
    switch (record.class) {
      case "workspace":
        return workspaceQuery.data ?? [];
      case "canvas":
        return canvasQuery.data ?? [];
      case "flow-session":
        return flowSessionQuery.data ?? [];
      default:
        return [];
    }
  }, [
    canvasQuery.data,
    flowSessionQuery.data,
    record.class,
    workspaceQuery.data,
  ]);
}

/**
 * Handler deps for the pane-header kebab — the same actions as the left-rail
 * file rows and the canvas node menu. The pane sits beside the open canvas
 * (`/canvas/:canvasId` stays the main route; the pane lives in location
 * state), so archiving a file placed there removes its node too, exactly as
 * the other two surfaces do. Matched off the URL, not `useParams`: the pane
 * router mounts outside the `<Routes>` that owns `:canvasId`.
 */
export function useFileRecordPaneHeaderKebabHandlerDeps(
  record: FileRecord,
): FileRecordActionHandlerDeps {
  const allRecords = useFileRecordSiblingList(record);
  const canvasId = useMatch(ROUTES.CANVAS.DETAIL)?.params.canvasId;
  const { fileRecordIdToNodeId } = useCanvasFileRecordNodeIds(canvasId);

  const { actions } = useFileRecordCardActions({
    workspaceId: record.workspace_id ?? undefined,
    allRecords,
    onFolderOpen: () => {},
    fileRecordIdToNodeId,
  });
  const { openFile, rename, copy, delete: remove } = actions;

  return useMemo(
    (): FileRecordActionHandlerDeps => ({
      onOpen: openFile,
      onRename: rename,
      onCopy: copy,
      onDelete: remove,
    }),
    [copy, openFile, remove, rename],
  );
}
